import {
    buildPrintReportModalTOCItems,
    PrintReportModalTOCItem,
    traversePrintReportModalTOCItem,
} from '../../data-types/printReportModalTOCItem';
import { TaxReturnTOC, TaxReturnTOCItem, TaxReturnTOCKey, TOCSelectOption } from '../../model';
import { isEmptyGuid } from '../../utils/GuidUtils';
import { traverseTaxReturnTOCItem } from '../../utils/TaxReturnTOCUtils';

export type PrintSelectionMode = 'visibleAndSelected' | 'matchSelectedFormPageId';

export type PrintAllColumnMode = 'omit' | 'false' | 'mirrorPrint';

export interface PrintApiPayloadOptions {
    selectionMode: PrintSelectionMode;
    allColumn: PrintAllColumnMode;
}

export interface PrintTOCLookupMaps {
    itemByNodeKey: Map<string, PrintReportModalTOCItem>;
    parentNodeKeyByNodeKey: Map<string, string | null>;
    childNodeKeysByNodeKey: Map<string, string[]>;
    tocKeyByNodeKey: Map<string, TaxReturnTOCKey>;
    visibleLeafNodeKeys: Set<string>;
    branchNodeKeys: Set<string>;
}

export const getFormTocDataAsRequest = (data: TaxReturnTOCItem[] | undefined): TOCSelectOption[] => {
    const tocSelectOptions: TOCSelectOption[] = [];

    if (!data) {
        return [];
    }

    for (const item of data) {
        traverseTaxReturnTOCItem(item, (tocItem: TaxReturnTOCItem) => {
            if (tocItem && tocItem.taxReturnTOCKey) {
                tocSelectOptions?.push({
                    print: tocItem.isSelectedForPrint,
                    tocKey: tocItem.taxReturnTOCKey,
                });
            }
        });
    }

    return tocSelectOptions;
};

export const getFormTocDataAsRequestByNodeKeys = (
    checkedStateByNodeKey: Map<string, boolean | 'mixed'>,
    tocKeyByNodeKey: Map<string, TaxReturnTOCKey>
): TOCSelectOption[] => {
    const tocSelectOptions: TOCSelectOption[] = [];

    for (const [nodeKey, tocKey] of tocKeyByNodeKey.entries()) {
        const checkedState: boolean | 'mixed' | undefined = checkedStateByNodeKey.get(nodeKey);
        const shouldPrint: boolean = checkedState === true;

        tocSelectOptions.push({
            print: shouldPrint,
            tocKey: tocKey,
        });
    }

    return tocSelectOptions;
};

export const buildPrintTOCLookupMaps = (tocData: TaxReturnTOCItem[]): PrintTOCLookupMaps => {
    const typedTocData: PrintReportModalTOCItem[] = tocData as PrintReportModalTOCItem[];
    const itemByNodeKey: Map<string, PrintReportModalTOCItem> = new Map<string, PrintReportModalTOCItem>();
    const parentNodeKeyByNodeKey: Map<string, string | null> = new Map<string, string | null>();
    const childNodeKeysByNodeKey: Map<string, string[]> = new Map<string, string[]>();
    const tocKeyByNodeKey: Map<string, TaxReturnTOCKey> = new Map<string, TaxReturnTOCKey>();
    const visibleLeafNodeKeys: Set<string> = new Set<string>();
    const branchNodeKeys: Set<string> = new Set<string>();

    const addNodeToMaps = (item: PrintReportModalTOCItem, parentNodeKey: string | null): void => {
        const nodeKey: string = item.nodeKey;
        if (!nodeKey) {
            return;
        }

        itemByNodeKey.set(nodeKey, item);
        parentNodeKeyByNodeKey.set(nodeKey, parentNodeKey);

        const childItems: PrintReportModalTOCItem[] = item.children ?? [];
        const childNodeKeys: string[] = childItems.map((childItem: PrintReportModalTOCItem) => childItem.nodeKey);

        childNodeKeysByNodeKey.set(nodeKey, childNodeKeys);

        if (childNodeKeys.length > 0) {
            branchNodeKeys.add(nodeKey);
        }

        if (childNodeKeys.length === 0 && (item.isVisibleInPrintView ?? false)) {
            visibleLeafNodeKeys.add(nodeKey);
        }

        if (item.taxReturnTOCKey) {
            tocKeyByNodeKey.set(nodeKey, item.taxReturnTOCKey);
        }

        for (const childItem of childItems) {
            addNodeToMaps(childItem, nodeKey);
        }
    };

    for (const item of typedTocData) {
        addNodeToMaps(item, null);
    }

    return {
        itemByNodeKey,
        parentNodeKeyByNodeKey,
        childNodeKeysByNodeKey,
        tocKeyByNodeKey,
        visibleLeafNodeKeys,
        branchNodeKeys,
    };
};

export const getBranchNodeKeys = (taxReturnTOCItems: PrintReportModalTOCItem[]): Set<string> => {
    const branchNodeKeys: Set<string> = new Set<string>();

    for (const item of taxReturnTOCItems) {
        traversePrintReportModalTOCItem(item, (tocItem: PrintReportModalTOCItem) => {
            const hasChildren: boolean = (tocItem.children?.length ?? 0) > 0;
            if (hasChildren) {
                branchNodeKeys.add(tocItem.nodeKey);
            }
        });
    }

    return branchNodeKeys;
};

export const getSelectedAndTotalItemsCount = (tocData: TaxReturnTOCItem[]) => {
    let selectedCount = 0;
    let totalCount = 0;

    for (const tocItem of tocData) {
        traverseTaxReturnTOCItem(tocItem, (item: TaxReturnTOCItem) => {
            if (!item.children || item.children.length === 0) {
                totalCount++;

                if (item.isSelectedForPrint) {
                    selectedCount++;
                }
            }
        });
    }

    return { selectedCount, totalCount };
};

export const getSelectedAndTotalItemsCountByNodeKeys = (selectedNodeKeys: Set<string>, visibleLeafNodeKeys: Set<string>) => {
    let selectedCount: number = 0;

    for (const visibleLeafNodeKey of visibleLeafNodeKeys) {
        if (selectedNodeKeys.has(visibleLeafNodeKey)) {
            selectedCount++;
        }
    }

    return {
        selectedCount,
        totalCount: visibleLeafNodeKeys.size,
    };
};

/**
 * Pure function that builds the API payload for a print request.
 *
 * Produces a TOCSelectOption for every leaf node (no children) that has a
 * taxReturnTOCKey. Every leaf is always emitted; only the print flag differs.
 *
 * selectionMode controls how print is derived:
 * - visibleAndSelected: print is true when the leaf is visible and selected.
 * - matchSelectedFormPageId: print is true when any selected leaf shares the
 *   same formPageId (visibility does not gate print).
 *
 * allColumn controls whether allColumn is emitted on each option:
 * - omit: allColumn is not included.
 * - false: allColumn is always false.
 * - mirrorPrint: allColumn mirrors print, except enumerated forms stay false.
 */
export const buildPrintApiPayload = (
    formTOCData: TaxReturnTOC | null | undefined,
    selectedNodeKeys: Set<string>,
    options: PrintApiPayloadOptions
): TOCSelectOption[] => {
    if (!formTOCData) {
        return [];
    }

    const tocItems: PrintReportModalTOCItem[] = buildPrintReportModalTOCItems(formTOCData.items ?? []);
    const { itemByNodeKey, tocKeyByNodeKey, childNodeKeysByNodeKey } = buildPrintTOCLookupMaps(tocItems);

    const result: TOCSelectOption[] = [];

    interface LeafNodeEntry {
        nodeKey: string;
        tocKey: TaxReturnTOCKey;
        isVisibleInPrintView: boolean;
        isEnumerated: boolean;
    }

    const leafNodeEntries: LeafNodeEntry[] = [];

    for (const [nodeKey, tocKey] of tocKeyByNodeKey.entries()) {
        const childNodeKeys: string[] = childNodeKeysByNodeKey.get(nodeKey) ?? [];
        const isLeaf: boolean = childNodeKeys.length === 0;

        if (!isLeaf) {
            continue;
        }

        const item: PrintReportModalTOCItem | undefined = itemByNodeKey.get(nodeKey);
        const isVisibleInPrintView: boolean = item?.isVisibleInPrintView ?? false;
        const isEnumerated: boolean = item?.isEnumerated ?? false;
        leafNodeEntries.push({ nodeKey, tocKey, isVisibleInPrintView, isEnumerated });
    }

    const useFormPageIdSelection: boolean = options.selectionMode === 'matchSelectedFormPageId';

    const selectedFormPageIds: Set<string> = new Set<string>();
    if (useFormPageIdSelection) {
        for (const entry of leafNodeEntries) {
            const formPageId: string | null | undefined = entry.tocKey.formPageId;
            if (formPageId && !isEmptyGuid(formPageId) && entry.isVisibleInPrintView && selectedNodeKeys.has(entry.nodeKey)) {
                selectedFormPageIds.add(formPageId);
            }
        }
    }

    leafNodeEntries.forEach((entry: LeafNodeEntry): void => {
        const isSelected: boolean = selectedNodeKeys.has(entry.nodeKey);
        const isVisibleInPrintView: boolean = entry.isVisibleInPrintView;
        const isEnumerated: boolean = entry.isEnumerated;
        const formPageId: string | null | undefined = entry.tocKey.formPageId;

        const print: boolean =
            useFormPageIdSelection && formPageId && !isEmptyGuid(formPageId)
                ? selectedFormPageIds.has(formPageId)
                : isVisibleInPrintView && isSelected;

        const option: TOCSelectOption = { print, tocKey: entry.tocKey };
        if (options.allColumn !== 'omit') {
            const canMirrorPrint: boolean = options.allColumn === 'mirrorPrint' && !isEnumerated;
            option.allColumn = canMirrorPrint ? print : false;
        }

        result.push(option);
    });

    return result;
};
